/**
 * Auth Orchestrator
 * Centralized OAuth flow management with finite-state machine
 * Handles all OAuth providers (Google, TikTok, X) through a unified interface
 */

import { supabase } from '@/integrations/supabase/client';
import { isNativeRuntime } from '@/lib/nativeRuntime';

// Auth States (Finite State Machine)
export type AuthState =
  | 'idle'
  | 'launching_provider'
  | 'waiting_callback'
  | 'exchanging_token'
  | 'fetching_profile'
  | 'linking_account'
  | 'signed_in'
  | 'error'
  | 'cancelled';

export type OAuthProvider = 'google' | 'tiktok' | 'x' | 'apple';

export interface AuthAttempt {
  id: string;
  provider: OAuthProvider;
  state: AuthState;
  startedAt: number;
  isNative: boolean;
  stepMessage: string;
  error?: AuthError;
}

export interface AuthError {
  code: string;
  userMessage: string;
  technicalMessage?: string;
  retryable: boolean;
}

export interface AuthResult {
  success: boolean;
  userId?: string;
  isNewUser?: boolean;
  error?: AuthError;
}

// Timing constants
export const AUTH_TIMEOUT_MS           = 45000;          // 45 seconds
export const SESSION_POLL_INTERVAL_MS  = 1500;
export const PENDING_ATTEMPT_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

// Storage keys
const PENDING_ATTEMPT_KEY = 'pending_auth_attempt';
const AUTH_SESSION_KEY    = 'auth_session_pending';

// Step messages for each state
const STEP_MESSAGES: Record<AuthState, string> = {
  idle:               '',
  launching_provider: 'Opening sign-in...',
  waiting_callback:   'Waiting for authentication...',
  exchanging_token:   'Verifying credentials...',
  fetching_profile:   'Fetching your profile...',
  linking_account:    'Setting up your account...',
  signed_in:          'Sign in successful!',
  error:              'Sign in failed',
  cancelled:          'Sign in cancelled',
};

/**
 * Generate a unique attempt ID
 */
export function generateAttemptId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get step message for a state
 */
export function getStepMessage(state: AuthState, provider?: OAuthProvider): string {
  if (state === 'launching_provider' && provider) {
    const providerNames: Record<OAuthProvider, string> = {
      google: 'Google',
      tiktok: 'TikTok',
      x:      'X',
      apple:  'Apple',
    };
    return `Opening ${providerNames[provider]}...`;
  }
  return STEP_MESSAGES[state];
}

/**
 * Create a new auth attempt
 */
export function createAuthAttempt(provider: OAuthProvider, isNative: boolean): AuthAttempt {
  return {
    id: generateAttemptId(),
    provider,
    state: 'idle',
    startedAt: Date.now(),
    isNative,
    stepMessage: '',
  };
}

/**
 * Transition to a new state with validation
 */
export function transitionState(
  attempt: AuthAttempt,
  newState: AuthState,
  error?: AuthError
): AuthAttempt {
  // Validate transition (basic state machine rules)
  const validTransitions: Record<AuthState, AuthState[]> = {
    idle:               ['launching_provider', 'error'],
    launching_provider: ['waiting_callback', 'error', 'cancelled'],
    waiting_callback:   ['exchanging_token', 'error', 'cancelled'],
    exchanging_token:   ['fetching_profile', 'linking_account', 'signed_in', 'error'],
    fetching_profile:   ['linking_account', 'signed_in', 'error'],
    linking_account:    ['signed_in', 'error'],
    signed_in:          ['idle'],
    error:              ['idle', 'launching_provider'],
    cancelled:          ['idle', 'launching_provider'],
  };

  const valid = validTransitions[attempt.state]?.includes(newState);
  if (!valid) {
    console.warn(`[AuthOrchestrator] Invalid transition: ${attempt.state} -> ${newState}`);
  }

  return {
    ...attempt,
    state:       newState,
    stepMessage: getStepMessage(newState, attempt.provider),
    // FIX: Original used `error || attempt.error`, which preserved the previous
    // AuthError when retrying after a failure (e.g., error → launching_provider).
    // Any consumer reading `attempt.error` after a retry would see stale error data.
    // Fix: only carry error data into the 'error' state; clear it for all others.
    error: newState === 'error' ? (error ?? attempt.error) : undefined,
  };
}

/**
 * Save pending auth attempt for cold-start recovery
 */
export function savePendingAttempt(attempt: AuthAttempt): void {
  try {
    const data = {
      ...attempt,
      savedAt: Date.now(),
    };
    localStorage.setItem(PENDING_ATTEMPT_KEY, JSON.stringify(data));
    console.log('[AuthOrchestrator] Saved pending attempt:', attempt.id);
  } catch (e) {
    console.warn('[AuthOrchestrator] Failed to save pending attempt:', e);
  }
}

/**
 * Get pending auth attempt (for cold-start recovery)
 */
export function getPendingAttempt(): AuthAttempt | null {
  try {
    const stored = localStorage.getItem(PENDING_ATTEMPT_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Check if attempt is still valid (within 5 minutes)
    if (Date.now() - data.savedAt > PENDING_ATTEMPT_MAX_AGE_MS) {
      console.log('[AuthOrchestrator] Pending attempt expired, clearing');
      clearPendingAttempt();
      return null;
    }

    return data as AuthAttempt;
  } catch {
    return null;
  }
}

/**
 * Clear pending auth attempt
 */
export function clearPendingAttempt(): void {
  try {
    localStorage.removeItem(PENDING_ATTEMPT_KEY);
    localStorage.removeItem(AUTH_SESSION_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Save session tokens for recovery
 */
export function savePendingSession(
  accessToken: string,
  refreshToken: string,
  provider: OAuthProvider
): void {
  try {
    const data = {
      accessToken,
      refreshToken,
      provider,
      savedAt: Date.now(),
    };
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(data));
    console.log('[AuthOrchestrator] Saved pending session for:', provider);
  } catch (e) {
    console.warn('[AuthOrchestrator] Failed to save pending session:', e);
  }
}

/**
 * Get pending session for recovery
 */
export function getPendingSession(): {
  accessToken: string;
  refreshToken: string;
  provider: OAuthProvider;
} | null {
  try {
    const stored = localStorage.getItem(AUTH_SESSION_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Session is only valid for 5 minutes
    if (Date.now() - data.savedAt > PENDING_ATTEMPT_MAX_AGE_MS) {
      localStorage.removeItem(AUTH_SESSION_KEY);
      return null;
    }

    return {
      accessToken:  data.accessToken,
      refreshToken: data.refreshToken,
      provider:     data.provider,
    };
  } catch {
    return null;
  }
}

/**
 * Recover pending session on app launch
 */
export async function recoverPendingSession(): Promise<boolean> {
  const pending = getPendingSession();
  if (!pending) return false;

  console.log('[AuthOrchestrator] Attempting to recover session for:', pending.provider);

  try {
    const { error } = await supabase.auth.setSession({
      access_token:  pending.accessToken,
      refresh_token: pending.refreshToken,
    });

    if (error) {
      console.error('[AuthOrchestrator] Session recovery failed:', error);
      clearPendingAttempt();
      return false;
    }

    console.log('[AuthOrchestrator] Session recovered successfully');
    clearPendingAttempt();
    return true;
  } catch (e) {
    console.error('[AuthOrchestrator] Session recovery error:', e);
    clearPendingAttempt();
    return false;
  }
}

function hasValidSubClaim(accessToken?: string | null): boolean {
  if (!accessToken) return false;
  try {
    const payloadPart = accessToken.split('.')[1];
    if (!payloadPart) return false;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return typeof payload?.sub === 'string' && payload.sub.length > 0;
  } catch {
    return false;
  }
}

async function clearCorruptSession(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } catch {
    // Ignore network/sign-out errors; we'll clear local auth cache below.
  }

  if (typeof localStorage !== 'undefined') {
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Ignore storage access failures.
    }
  }
}

/**
 * Check if user is currently authenticated
 */
export async function checkSession(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    if (!hasValidSubClaim(session.access_token)) {
      console.warn('[AuthOrchestrator] Invalid session token (missing sub) detected — clearing session');
      await clearCorruptSession();
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Check if attempt has timed out
 */
export function isAttemptTimedOut(attempt: AuthAttempt): boolean {
  return Date.now() - attempt.startedAt > AUTH_TIMEOUT_MS;
}

/**
 * Check network connectivity
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Get current native runtime status
 */
export function getIsNative(): boolean {
  return isNativeRuntime();
}

/**
 * Parse error to user-friendly format
 */
export function parseAuthError(error: unknown): AuthError {
  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Network errors
    if (message.includes('network') || message.includes('fetch') || message.includes('offline')) {
      return {
        code: 'network_error',
        userMessage: 'No internet connection. Please check your network and try again.',
        technicalMessage: error.message,
        retryable: true,
      };
    }

    // Timeout
    if (message.includes('timeout') || message.includes('timed out')) {
      return {
        code: 'timeout',
        userMessage: 'Sign in took too long. Please try again.',
        technicalMessage: error.message,
        retryable: true,
      };
    }

    // Invalid grant (expired code)
    if (message.includes('invalid_grant') || message.includes('expired')) {
      return {
        code: 'invalid_grant',
        userMessage: 'Your sign-in session expired. Please try again.',
        technicalMessage: error.message,
        retryable: false,
      };
    }

    // State mismatch (potential CSRF)
    if (message.includes('state') && (message.includes('mismatch') || message.includes('invalid'))) {
      return {
        code: 'state_mismatch',
        userMessage: "Sign-in couldn't be verified. Please try again.",
        technicalMessage: error.message,
        retryable: false,
      };
    }

    // Access denied (user cancelled at provider)
    if (message.includes('access_denied') || message.includes('cancelled') || message.includes('canceled')) {
      return {
        code: 'cancelled',
        userMessage: 'Sign-in was cancelled.',
        technicalMessage: error.message,
        retryable: true,
      };
    }

    // Rate limited
    if (message.includes('rate') || message.includes('429') || message.includes('too many')) {
      return {
        code: 'rate_limited',
        userMessage: 'Too many attempts. Please wait a moment and try again.',
        technicalMessage: error.message,
        retryable: true,
      };
    }

    // Server errors
    if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('server')) {
      return {
        code: 'server_error',
        userMessage: 'The sign-in service is temporarily unavailable. Please try again.',
        technicalMessage: error.message,
        retryable: true,
      };
    }

    // FIX: Original returned error.message directly as userMessage when length < 100.
    // Technical messages like "Missing code verifier for PKCE flow" or "Failed to
    // load X auth module" would surface directly to users. Fall through to the
    // generic fallback — technicalMessage preserves the raw message for debugging.
    return {
      code: 'unknown',
      userMessage: 'Something went wrong. Please try again.',
      technicalMessage: error.message,
      retryable: true,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    // FIX: Same issue — raw string shown as userMessage when length < 100.
    // Use generic message; keep original as technicalMessage.
    return {
      code: 'unknown',
      userMessage: 'Something went wrong. Please try again.',
      technicalMessage: error,
      retryable: true,
    };
  }

  // Default fallback
  return {
    code: 'unknown',
    userMessage: 'Something went wrong. Please try again.',
    technicalMessage: String(error),
    retryable: true,
  };
}

/**
 * Clean up all auth state
 */
export function cleanupAuthState(): void {
  clearPendingAttempt();
  // Also clear provider-specific state
  try {
    sessionStorage.removeItem('tiktok_oauth_state');
    localStorage.removeItem('tiktok_oauth_state');
    sessionStorage.removeItem('x_oauth_state');
    localStorage.removeItem('x_oauth_state');
    localStorage.removeItem('oauth_attempt');
    localStorage.removeItem('pending_oauth_session');
  } catch {
    // Ignore storage errors
  }
}
